import React, { useState, useCallback } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

// Dialog Context
const DialogContext = React.createContext();

export const DialogProvider = ({ children }) => {
    const [dialogs, setDialogs] = useState([]);

    const openDialog = useCallback((config) => {
        console.log('🔧 Opening dialog with config:', config);
        const id = Math.random().toString(36).substr(2, 9);
        const dialog = {
            id,
            ...config,
            isOpen: true
        };
        console.log('🔧 Dialog object:', dialog);
        setDialogs(prev => {
            const newDialogs = [...prev, dialog];
            console.log('🔧 Updated dialogs array:', newDialogs);
            return newDialogs;
        });
        return id;
    }, []);

    const closeDialog = useCallback((id) => {
        setDialogs(prev => prev.filter(dialog => dialog.id !== id));
    }, []);

    const closeAllDialogs = useCallback(() => {
        setDialogs([]);
    }, []);

    return (
        <DialogContext.Provider value={{ openDialog, closeDialog, closeAllDialogs }}>
            {children}
            {dialogs.map(dialog => {
                console.log('🔧 Rendering dialog:', dialog.id, 'variant:', dialog.variant, 'type:', dialog.type, 'isOpen:', dialog.isOpen, 'full dialog:', dialog);
                // Check for variant OR type === 'confirm' to handle both old and new formats
                if (dialog.variant || dialog.type === 'confirm') {
                    // This is a ConfirmationDialog
                    // Map old format (type: 'confirm', message) to new format (variant, description)
                    const confirmationProps = {
                        ...dialog,
                        variant: dialog.variant || 'danger',
                        description: dialog.description || dialog.message || 'Are you sure you want to proceed?',
                        confirmText: dialog.confirmText || 'Confirm',
                        cancelText: dialog.cancelText || 'Cancel'
                    };
                    return (
                        <ConfirmationDialog 
                            key={dialog.id} 
                            {...confirmationProps} 
                            onClose={() => closeDialog(dialog.id)} 
                        />
                    );
                } else {
                    // This is a regular Dialog
                    return (
                        <Dialog key={dialog.id} {...dialog} onClose={() => closeDialog(dialog.id)}>
                            {dialog.children || dialog.message || dialog.description}
                        </Dialog>
                    );
                }
            })}
        </DialogContext.Provider>
    );
};

export const useDialog = () => {
    const context = React.useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};

// Main Dialog Component
export const Dialog = ({ 
    isOpen, 
    onClose, 
    title, 
    description, 
    children, 
    size = 'md',
    showCloseButton = true,
    className = '',
    closeOnOverlayClick = true
}) => {
    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        full: 'max-w-full mx-4'
    };

    // For full-size modals, don't close on overlay click
    const shouldCloseOnOverlayClick = closeOnOverlayClick && size !== 'full';

    return (
        <div className="fixed inset-0 z-[10000] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                {size !== 'full' && (
                    <div 
                        className="fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity z-[10000] pointer-events-auto"
                        onClick={shouldCloseOnOverlayClick ? onClose : undefined}
                    />
                )}
                <div 
                    className={`relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full z-[10001] pointer-events-auto ${sizeClasses[size]} ${className}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ minWidth: '320px' }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

// Dialog Header
export const DialogHeader = ({ title, onClose, showCloseButton = true, className = '' }) => (
    <div className={`flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
        </h3>
        {showCloseButton && (
            <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
                <XMarkIcon className="h-6 w-6" />
            </button>
        )}
    </div>
);

// Dialog Body
export const DialogBody = ({ children, className = '' }) => (
    <div className={`p-6 ${className}`}>
        {children}
    </div>
);

// Dialog Footer
export const DialogFooter = ({ children, className = '' }) => (
    <div className={`flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 ${className}`}>
        {children}
    </div>
);

// Confirmation Dialog
export const ConfirmationDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Confirm Action",
    description = "Are you sure you want to proceed?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "danger",
    isLoading = false,
    className = ""
}) => {
    const variantClasses = {
        danger: {
            button: "bg-red-600 hover:bg-red-700 text-white",
            icon: "text-red-600"
        },
        warning: {
            button: "bg-yellow-600 hover:bg-yellow-700 text-white",
            icon: "text-yellow-600"
        },
        info: {
            button: "bg-blue-600 hover:bg-blue-700 text-white",
            icon: "text-blue-600"
        },
        success: {
            button: "bg-green-600 hover:bg-green-700 text-white",
            icon: "text-green-600"
        },
        error: {
            button: "bg-red-600 hover:bg-red-700 text-white",
            icon: "text-red-600"
        }
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Dialog isOpen={isOpen} onClose={onClose} size="md" className={className}>
            <DialogHeader title={title} onClose={onClose} />
            <DialogBody>
                <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 ${variantClasses[variant]?.icon || variantClasses.danger.icon}`}>
                        <ExclamationTriangleIcon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                            {description}
                        </p>
                    </div>
                </div>
            </DialogBody>
            <DialogFooter>
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    disabled={isLoading}
                >
                    {cancelText}
                </button>
                <button
                    onClick={handleConfirm}
                    className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${variantClasses[variant]?.button || variantClasses.danger.button}`}
                    disabled={isLoading}
                >
                    {isLoading ? 'Processing...' : confirmText}
                </button>
            </DialogFooter>
        </Dialog>
    );
};

// Alert Dialog
export const AlertDialog = ({
    isOpen,
    onClose,
    title,
    description,
    buttonText = "OK",
    variant = "info",
    className = ""
}) => {
    const variantClasses = {
        success: {
            icon: "text-green-600",
            button: "bg-green-600 hover:bg-green-700 text-white"
        },
        error: {
            icon: "text-red-600",
            button: "bg-red-600 hover:bg-red-700 text-white"
        },
        warning: {
            icon: "text-yellow-600",
            button: "bg-yellow-600 hover:bg-yellow-700 text-white"
        },
        info: {
            icon: "text-blue-600",
            button: "bg-blue-600 hover:bg-blue-700 text-white"
        }
    };

    return (
        <Dialog isOpen={isOpen} onClose={onClose} size="sm" className={className}>
            <DialogHeader title={title} onClose={onClose} />
            <DialogBody>
                <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 ${variantClasses[variant].icon}`}>
                        <ExclamationTriangleIcon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm text-foreground">
                            {description}
                        </p>
                    </div>
                </div>
            </DialogBody>
            <DialogFooter>
                <button
                    onClick={onClose}
                    className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${variantClasses[variant].button}`}
                >
                    {buttonText}
                </button>
            </DialogFooter>
        </Dialog>
    );
};

